export function DocumentContent() {
  return (
    <>
      <p>
        These terms (the &quot;<strong>Agreement</strong>&quot;) govern the use
        of the Product (as defined in Section&nbsp;13) provided by Provider and
        set forth the rights and obligations between Provider and Customer. By
        signing an Order Form that references this Agreement, or by otherwise
        accessing or using the Product, Customer agrees to be bound by this
        Agreement.
      </p>
      <hr />
      <h2 id="1-service">
        <span>1.</span> Service
      </h2>
      <div className="indent">
        <h4 id="1-1-access-and-use">
          <span>1.1</span> Access and Use
        </h4>
        <p>
          During the Subscription Period and subject to the terms of this
          Agreement, Customer may (a) access and use the Product; and (b) copy
          and use the included Software and Documentation only as needed to
          access and use the Product, in each case, for its internal business
          purposes. The Product is made available to individuals and entities
          seeking the services described in the Order Form. Customer's rights to
          use the Product are automatically revoked if Customer attempts to use
          the Product for any purpose not expressly permitted by this Agreement.
          If a Customer Affiliate enters a separate Order Form with Provider,
          that Order Form creates a separate agreement between Provider and that
          Affiliate; Provider's responsibility to that Affiliate is individual
          and separate from Customer, and Customer is not responsible for its
          Affiliates' agreement.
        </p>
        <h4 id="1-2-support">
          <span>1.2</span> Support
        </h4>
        <p>
          During the Subscription Period, Provider will provide Technical
          Support as described in the applicable Order Form.
        </p>
        <h4 id="1-3-user-accounts">
          <span>1.3</span> User Accounts
        </h4>
        <p>
          Customer is responsible for all actions on Users' accounts and for all
          Users' compliance with this Agreement. Customer and Users must protect
          the confidentiality of their passwords and login credentials. Customer
          will promptly notify Provider if it suspects or knows of any
          fraudulent activity with its accounts, passwords, or credentials, or
          if they become compromised.
        </p>
        <h4 id="1-4-feedback-and-usage-data">
          <span>1.4</span> Feedback and Usage Data
        </h4>
        <p>
          Customer may, but is not required to, give Provider Feedback, in which
          case Customer gives Feedback “AS IS.” Provider may use all Feedback
          freely without any restriction or obligation, and Customer hereby
          assigns all right, title, and interest in and to such Feedback
          (including all intellectual property rights) to Provider.
        </p>
        <p>
          Provider may collect and analyze Usage Data, and Provider may freely
          use Usage Data to maintain, improve, enhance, and promote Provider's
          products and services without restriction or obligation. However,
          Provider may only disclose Usage Data to others if the Usage Data is
          aggregated and does not identify Customer or Users.
        </p>
        <h4 id="1-5-customer-content">
          <span>1.5</span> Customer Content
        </h4>
        <p>
          Provider may copy, display, modify, and use Customer Content only as
          needed to provide and maintain the Product and related offerings.
          Customer is responsible for the accuracy and content of Customer
          Content and represents and warrants that it has all rights necessary
          to submit or make Customer Content available to the Product and allow
          its use as described in this Agreement.
        </p>
        <h4 id="1-6-machine-learning">
          <span>1.6</span> Machine Learning and Data Ownership
        </h4>
        <div className="indent">
          <p>
            Customer retains exclusive ownership of all Customer Content and
            data submitted to the Product. Provider maintains strict logical
            isolation of Customer's data with no commingling between different
            customer environments.
          </p>

          <p>
            Unless Customer explicitly opts in through their account settings or
            an Order Form:
          </p>
          <ol className="list-decimal pl-6" type="a">
            <li>
              Provider will only use Customer's data to train or improve AI or
              machine learning models that are exclusively for that Customer's
              use;
            </li>
            <li>
              Provider will not use Customer's data to develop or enhance
              features that benefit other customers;
            </li>
            <li>
              Provider will not pool or combine Customer's data with other
              customers' data for any purpose; and
            </li>
            <li>
              Provider will only use Customer's data to provide and maintain the
              Product for Customer's use.
            </li>
          </ol>

          <p>
            The Product may include pre-trained artificial intelligence or
            machine learning features. Due to the nature of these technologies,
            information generated by these features may be incorrect or
            inaccurate. These features are not a substitute for human oversight
            and can be disabled by Customer at any time.
          </p>
        </div>
        <h4 id="1-7-age-requirements">
          <span>1.7</span> Age Requirements
        </h4>
        <p>
          The Product is not for use by anyone under the age of sixteen (16). If
          local laws require a higher minimum age for Provider to lawfully
          provide the Product to a User, that higher age will apply. If a User
          is under the age of eighteen (18) (or the age of majority in the
          User's jurisdiction), the User must only use the Product under the
          supervision of a parent, legal guardian, or other responsible adult.
        </p>
        <h4 id="1-8-use-of-the-services">
          <span>1.8</span> Use of the Services
        </h4>
        <p>
          Customer may only use the Product for the purposes set forth in this
          Agreement. Any attempt to use the Product for an unauthorized purpose,
          or in violation of this Agreement, is strictly prohibited.
        </p>
        <h4 id="1-9-communication-consent">
          <span>1.9</span> Communication Consent
        </h4>
        <p>
          Customer agrees that Provider may communicate with Customer (directly
          or through a third party) through the Product, or by email, phone, or
          postal mail, using the contact information Customer provides or that
          Provider obtains from third parties. To revoke consent at any time,
          Customer may contact Provider at the email or address provided in the
          Notices section of this Agreement.
        </p>

        <h4 id="1-10-service-levels">
          <span>1.10</span> Service Levels
        </h4>
        <p>
          Provider will make the Product available with a Monthly Uptime
          Percentage of at least 99.9% ("Service Level Standard").
        </p>

        <h4 id="1-11-api-usage">
          <span>1.11</span> API Usage
        </h4>
        <p>
          If the Product includes API access, Provider will provide Customer
          with necessary API credentials. Customer must keep API credentials
          secure and is responsible for all activities that occur using its API
          credentials.
        </p>

        <p>Unless otherwise specified in an Order Form:</p>
        <ol className="list-decimal pl-6" type="a">
          <li>
            API calls are subject to rate limiting as agreed in your Order Form;
          </li>
          <li>Provider may adjust rate limits with 30 days' notice; and</li>
          <li>
            Provider will use commercially reasonable efforts to accommodate
            higher limits upon request.
          </li>
        </ol>

        <h4 id="1-12-third-party">
          <span>1.12</span> Third-Party Services and Dependencies
        </h4>
        <p>
          The Product may use or depend on third-party services, software, or
          infrastructure ("Third-Party Services"). Provider makes no warranties
          or guarantees about the availability, reliability, or accuracy of
          Third-Party Services. Changes to Third-Party Services may affect the
          Product's functionality. Provider may modify, replace, or discontinue
          the use of any Third-Party Services upon notice to Customer.
        </p>

        <h4 id="1-13-subcontractors">
          <span>1.13</span> Subcontractors and Service Providers
        </h4>
        <p>
          Provider may engage subcontractors, including independent coaches and
          consultants, to assist in providing the Product and related services.
          Provider remains responsible for its subcontractors' compliance with
          this Agreement and for the overall provision of the Product. All
          subcontractors will be bound by confidentiality obligations at least
          as protective as those in this Agreement. Provider will ensure all
          subcontractors meet appropriate qualification and vetting standards.
        </p>
      </div>
      <hr />
      <h2 id="2-restrictions-obligations">
        <span>2.</span> Restrictions &amp; Obligations
      </h2>
      <div className="indent">
        <h4 id="2-1-restrictions-on-customer">
          <span>2.1</span> Restrictions on Customer
        </h4>
        <ol>
          <li>
            <div>
              <strong>General Prohibitions.</strong> Except as expressly
              permitted by this Agreement, Customer will not (and will not allow
              anyone else to):
            </div>
            <ol className="list-decimal pl-6" type="i">
              <li>
                reverse engineer, decompile, or attempt to discover any source
                code or underlying ideas or algorithms of the Product (except to
                the extent Applicable Laws prohibit this restriction);
              </li>
              <li>
                provide, sell, transfer, sublicense, lend, distribute, rent, or
                otherwise allow others to access or use the Product;
              </li>
              <li>remove any proprietary notices or labels;</li>
              <li>copy, modify, or create derivative works of the Product;</li>
              <li>
                conduct security or vulnerability tests on, interfere with the
                operation of, cause performance degradation of, or circumvent
                access restrictions of the Product;
              </li>
              <li>
                access accounts, information, data, or portions of the Product
                to which Customer does not have explicit authorization;
              </li>
              <li>
                use the Product to develop or assist in developing a competing
                service or product;
              </li>
              <li>
                use the Product with any High Risk Activities or with any
                activity prohibited by Applicable Laws;
              </li>
              <li>
                use the Product to obtain unauthorized access to anyone else's
                networks or equipment;
              </li>
              <li>
                upload, submit, or otherwise make available to the Product any
                Customer Content to which Customer and Users do not have the
                proper rights.
              </li>
            </ol>
          </li>
          <li>
            <div>
              <strong>Prohibited Code and Content.</strong> Customer will not
              transmit or upload to the Product any code that is designed to
              damage, disrupt, disable, harm, impair, interfere with, intercept,
              expropriate, or otherwise impede in any manner any data, storage
              media, program, system, equipment, or communication. Customer will
              not transmit or upload any content that is false, defamatory,
              harassing, abusive, fraudulent, infringing, obscene, or otherwise
              objectionable, including content that may be harmful to minors, or
              content that Customer does not have the right to disclose.
            </div>
          </li>
          <li>
            <div>
              <strong>Prohibited Automated Access.</strong> Use of any automated
              system or software to crawl, scrape, or extract data from the
              Product without Provider's express written permission is strictly
              prohibited.
            </div>
          </li>
          <li>
            <div>
              <strong>Prohibited Competitive Uses.</strong> Customer will not
              use the Product (a) to develop, launch, or assist in the
              development or launch of a product or service that competes with
              any aspect of the Product or Provider's business; (b) to analyze,
              assess, or reverse engineer the Product for purposes of competing
              with Provider; (c) to collect, analyze, or use information about
              the Product's features or functionality to create or enhance
              competing products or services; or (d) to assist, encourage, or
              enable others to do any of the above.
            </div>
          </li>
          <li>
            <div>
              <strong>Documentation and Use Limitations.</strong> Use of the
              Product must comply with all Documentation and any applicable use
              limitations described in an Order Form.
            </div>
          </li>
        </ol>
      </div>
      <hr />
      <h2 id="3-privacy-security">
        <span>3.</span> Privacy &amp; Security
      </h2>
      <div className="indent">
        <h4 id="3-1-personal-data">
          <span>3.1</span> Personal Data
        </h4>
        <p>
          Before submitting Personal Data governed by GDPR, Customer must enter
          into a data processing agreement with Provider. If the parties have a
          data processing agreement (“DPA”), each party will comply with its
          obligations in the DPA, the terms of the DPA will control each party's
          rights and obligations as to Personal Data, and the terms of the DPA
          will control in the event of any conflict with this Agreement.
        </p>
        <h4 id="3-2-prohibited-data">
          <span>3.2</span> Prohibited Data
        </h4>
        <p>
          Customer will not (and will not allow anyone else to) submit
          Prohibited Data to the Product unless authorized by the Order Form or
          Key Terms.
        </p>
        <h4 id="3-3-data-sharing">
          <span>3.3</span> Data Sharing
        </h4>
        <p>
          If Customer uses the Product, Customer agrees and consents to Provider
          sharing necessary information (including personal data) with
          Provider's coaches, contractors, and other third parties for the
          purpose of delivering services required to provide and improve the
          Product. Customer further agrees and consents to those third parties
          using and sharing the necessary information internally and with others
          in order to assist Provider in providing the Product.
        </p>
      </div>
      <hr />
      <h2 id="4-payment-taxes">
        <span>4.</span> Payment &amp; Taxes
      </h2>
      <div className="indent">
        <h4 id="4-1-fees">
          <span>4.1</span> Fees
        </h4>
        <p>
          Unless the Order Form specifies a different currency, all Fees are in
          U.S. Dollars and are inclusive of taxes. Provider may store Customer's
          payment information (including credit card or bank account details) to
          process Fees. For any credit card charges in excess of $10,000 USD,
          Provider may charge a 2.9% processing fee. Except where otherwise
          provided in this Agreement (including any rights to a prorated refund
          upon certain terminations or as described in Section 4.8 (Refunds)),
          Fees are otherwise non-refundable.
        </p>
        <h4 id="4-2-invoicing">
          <span>4.2</span> Invoicing
        </h4>
        <p>
          For a Payment Process with invoicing, Provider will send invoices for
          usage-based Fees in arrears and for all other Fees in advance, in each
          case according to the Payment Process.
        </p>
        <h4 id="4-3-automatic-payment">
          <span>4.3</span> Automatic Payment
        </h4>
        <p>
          For a Payment Process with automatic payment, Provider will
          automatically charge the credit card, debit card, or other payment
          method on file for Fees according to the Payment Process, and Customer
          authorizes all such charges. In this case, Provider will make a copy
          of Customer's bills or transaction history available to Customer.
        </p>
        <h4 id="4-4-taxes">
          <span>4.4</span> Taxes
        </h4>
        <p>
          Customer is responsible for all duties, taxes, and levies that apply
          to Fees, including sales, use, VAT, GST, or withholding, that Provider
          itemizes and includes in an invoice. However, Customer is not
          responsible for Provider's income taxes.
        </p>
        <h4 id="4-5-payment">
          <span>4.5</span> Payment
        </h4>
        <p>
          Customer will pay Provider Fees and taxes in U.S. Dollars, unless the
          Order Form specifies a different currency, according to the Payment
          Process.
        </p>
        <h4 id="4-6-payment-dispute">
          <span>4.6</span> Payment Dispute
        </h4>
        <p>
          If Customer has a good-faith disagreement about the Fees charged or
          invoiced, Customer must notify Provider about the dispute before
          payment is due, or within 30 days of an automatic payment, and must
          pay all undisputed amounts on time. The parties will work together to
          resolve the dispute within 15 days. If no resolution is agreed, each
          party may pursue any remedies available under this Agreement or
          Applicable Laws.
        </p>
        <h4 id="4-7-credits">
          <span>4.7</span> Credits
        </h4>
        <p>Unless otherwise specified in an Order Form:</p>
        <ol className="list-decimal pl-6" type="a">
          <li>
            Credits are valid for 12 months from their respective purchase
            dates;
          </li>
          <li>Credits are used on a first-in-first-out (FIFO) basis;</li>
          <li>
            Unused credits will expire automatically at the end of their
            12-month validity period;
          </li>
          <li>
            Each credit purchase starts its own 12-month validity period; and
          </li>
          <li>Partially used credit packages will expire proportionally.</li>
        </ol>

        <p>
          Provider will maintain a record of credit purchase dates and
          quantities, usage and remaining balances, and upcoming expiration
          dates. Customer may access this information through their account
          dashboard or by request.
        </p>
        <h4 id="4-8-refunds">
          <span>4.8</span> Refunds
        </h4>
        <p>
          A credit purchase is eligible for a 75% refund of the original
          purchase price if (a) Customer requests the refund within 60 days of
          the purchase date, and (b) none of the credits have been used. Orders
          are not eligible for partial refunds of used credits. For credits
          purchased as a gift, only the original purchaser may initiate a refund
          and only if the recipient has not redeemed the credits.
        </p>

        <h4 id="4-9-fee-increases">
          <span>4.9</span> Fee Increases
        </h4>
        <p>
          Provider may change the Fees for the Product by providing at least
          sixty (60) days' advance notice to Customer before the start of the
          next Subscription Period. For usage-based Fees, Provider may modify
          the Fee structure by providing at least thirty (30) days' advance
          notice, except as otherwise restricted in the Order Form. If Customer
          continues to use the Product after the notice period, such use
          constitutes acceptance of the new Fees.
        </p>
      </div>
      <hr />
      <h2 id="5-term-termination">
        <span>5.</span> Term &amp; Termination
      </h2>
      <div className="indent">
        <h4 id="5-1-order-form-and-agreement">
          <span>5.1</span> Order Form and Agreement
        </h4>
        <p>
          For each Order Form, this Agreement will start on the Order Date,
          continue through the Subscription Period, and automatically renew for
          additional Subscription Periods unless one party gives notice of
          non-renewal to the other party before the Non-Renewal Notice Date.
        </p>
        <h4 id="5-2-duration-of-these-terms">
          <span>5.2</span> Duration of These Terms
        </h4>
        <p>
          These Terms will start on the Effective Date and continue for the
          longer of one year or until all Order Forms governed by these Terms
          have ended.
        </p>
        <h4 id="5-3-termination">
          <span>5.3</span> Termination
        </h4>
        <p>
          <strong>Termination for Cause.</strong> Either party may terminate
          these Terms or an Order Form immediately:
        </p>
        <ol className="list-decimal pl-6" type="a">
          <li>
            if the other party fails to cure a material breach of these Terms or
            an Order Form following 30 days' notice;
          </li>
          <li>
            upon notice if the other party (i) materially breaches these Terms
            or an Order Form in a manner that cannot be cured; (ii) dissolves or
            stops conducting business without a successor; (iii) makes an
            assignment for the benefit of creditors; or (iv) becomes the debtor
            in insolvency, receivership, or bankruptcy proceedings that continue
            for more than 60 days.
          </li>
        </ol>
        <p>
          <strong>Provider's Right to Terminate At Will.</strong> Provider may
          terminate Customer's access to the Product immediately for unpaid
          services or trial periods. For paid subscription periods, Provider may
          terminate with 30 days' notice if required by law or if Provider is
          ceasing to offer the Product generally to all customers. In such
          cases, Provider will issue any refund required by law, or as otherwise
          specified in an Order Form or this Agreement.
        </p>
        <h4 id="5-4-force-majeure">
          <span>5.4</span> Force Majeure
        </h4>
        <p>
          Either party may terminate an affected Order Form upon notice if a
          Force Majeure Event prevents the Product from materially operating for
          30 or more consecutive days. Provider will pay to Customer a prorated
          refund of any prepaid Fees for the remainder of the Subscription
          Period. A Force Majeure Event does not excuse Customer's obligation to
          pay Fees accrued prior to termination.
        </p>
        <h4 id="5-5-effect-of-termination">
          <span>5.5</span> Effect of Termination
        </h4>
        <p>Upon any expiration or termination:</p>
        <ol>
          <li>Customer will no longer have any right to use the Product. </li>
          <li>
            Upon Customer's request, Provider will delete Customer Content
            within 60 days.
          </li>
          <li>
            Each Recipient will return or destroy Discloser's Confidential
            Information in its possession or control.
          </li>
          <li>
            Provider will submit a final bill or invoice for all outstanding
            Fees accrued before termination, and Customer will pay the invoice
            according to Section 4 (Payment &amp; Taxes).
          </li>
        </ol>
        <h4 id="5-6-survival">
          <span>5.6</span> Survival
        </h4>
        <ol>
          <li>
            The following sections will survive expiration or termination of
            this Agreement: Section 1.4 (Feedback and Usage Data), Section 1.6
            (Machine Learning), Section 2.1 (Restrictions on Customer), Section
            4 (Payment &amp; Taxes) for Fees accrued or payable before
            expiration or termination, Section 5.5 (Effect of Termination),
            Section 5.6 (Survival), Section 6 (Representations &amp;
            Warranties), Section 7 (Disclaimer of Warranties), Section 8
            (Limitation of Liability), Section 9 (Indemnification), Section 10
            (Confidentiality), Section 11 (Reservation of Rights), Section 12
            (General Terms), Section 13 (Definitions), and any portions of an
            Order Form or other parts of this Agreement referenced by these
            sections.
          </li>
          <li>
            Each Recipient may retain Discloser's Confidential Information in
            accordance with its standard backup or record retention policies
            maintained in the ordinary course of business or as required by
            Applicable Laws, in which case Section 3 (Privacy &amp; Security)
            and Section 10 (Confidentiality) will continue to apply to retained
            Confidential Information.
          </li>
        </ol>
      </div>
      <hr />
      <h2 id="6-representations-warranties">
        <span>6.</span> Representations &amp; Warranties
      </h2>
      <div className="indent">
        <h4 id="6-1-mutual">
          <span>6.1</span> Mutual
        </h4>
        <p>Each party represents and warrants to the other that:</p>
        <ol>
          <li>
            it has the legal power and authority to enter into this Agreement;
          </li>
          <li>
            it is duly organized, validly existing, and in good standing under
            the Applicable Laws of the jurisdiction of its origin;
          </li>
          <li>
            it will comply with all Applicable Laws in performing its
            obligations or exercising its rights under this Agreement; and
          </li>
          <li>
            it will comply with any Additional Warranties stated in an Order
            Form (if applicable).
          </li>
        </ol>
        <h4 id="6-2-from-customer">
          <span>6.2</span> From Customer
        </h4>
        <p>
          Customer represents and warrants that it, all Users, and anyone
          submitting Customer Content each have and will continue to have all
          rights necessary to submit or make available Customer Content to the
          Product and to allow the use of Customer Content as described in this
          Agreement.
        </p>
        <h4 id="6-3-from-provider">
          <span>6.3</span> From Provider
        </h4>
        <p>
          Provider represents and warrants to Customer that it will not
          materially reduce the core functionality of the Cloud Service during
          the applicable Subscription Period without providing at least sixty
          (60) days' advance notice. Provider may modify, enhance, or deprecate
          specific features or capabilities to improve the overall Product,
          security, or performance. Such modifications will not be considered a
          breach of this warranty if reasonable alternatives or replacements are
          provided.
        </p>
        <h4 id="6-4-provider-warranty-remedy">
          <span>6.4</span> Provider Warranty Remedy
        </h4>
        <p>
          If Provider breaches the warranty in Section 6.3, Customer must give
          Provider notice (with enough detail for Provider to understand or
          replicate the issue) within 45 days of discovering the issue. Within
          45 days of receiving sufficient details of the warranty issue,
          Provider will attempt to restore the general functionality of the
          Cloud Service. If Provider cannot resolve the issue, Customer may
          terminate the affected Order Form and Provider will pay to Customer a
          prorated refund of prepaid Fees for the remainder of the Subscription
          Period. Provider's restoration obligation, and Customer's termination
          right, are Customer's only remedies if Provider does not meet the
          warranty in Section 6.3.
        </p>
      </div>
      <hr />
      <h2 id="7-disclaimer-of-warranties">
        <span>7.</span> Disclaimer of Warranties
      </h2>
      <p>
        Provider makes no guarantees that the Product will always be safe,
        secure, uninterrupted, or error-free, or that it will function without
        disruptions, delays, or imperfections. Except for the express warranties
        in Section 6 (Representations &amp; Warranties), Provider disclaims all
        other warranties and conditions, whether express or implied, including
        implied warranties and conditions of merchantability, fitness for a
        particular purpose, title, and non-infringement, to the fullest extent
        permitted by Applicable Laws. Customer acknowledges that any use of or
        reliance on the Product is at Customer's sole risk. The Product is
        provided on an “AS IS” and “AS AVAILABLE” basis.
      </p>
      <hr />
      <h2 id="8-limitation-of-liability">
        <span>8.</span> Limitation of Liability
      </h2>
      <div className="indent">
        <h4 id="8-1-liability-cap">
          <span>8.1</span> Liability Cap
        </h4>
        <p>
          Except as provided in Section 8.4 (Exceptions), the total cumulative
          liability of Provider and its Affiliates to Customer for all claims
          arising out of or relating to this Agreement will not exceed the
          greater of:
        </p>
        <ol className="list-decimal pl-6" type="a">
          <li>
            the total fees paid by Customer to Provider during the twelve (12)
            month period immediately preceding the incident giving rise to the
            claim; or
          </li>
          <li>$10,000 USD.</li>
        </ol>
        <p>
          For purposes of calculating the liability cap, the relevant fees shall
          include all amounts actually paid by Customer, excluding taxes, for
          the Product during the applicable period.
        </p>
        <h4 id="8-2-damages-waiver">
          <span>8.2</span> Damages Waiver
        </h4>
        <p>
          <strong>
            Except as provided in Section 8.4 (Exceptions), under no
            circumstances will either party be liable to the other for lost
            profits or revenues (whether direct or indirect), or for
            consequential, special, indirect, exemplary, punitive, or incidental
            damages relating to this Agreement, even if advised of the
            possibility of such damages.
          </strong>
        </p>
        <h4 id="8-3-applicability">
          <span>8.3</span> Applicability
        </h4>
        <p>
          The limitations and waivers contained in Sections 8.1 and 8.2 apply to
          all liability, whether in tort (including negligence), contract,
          breach of statutory duty, or otherwise.
        </p>
        <h4 id="8-4-exceptions">
          <span>8.4</span> Exceptions
        </h4>
        <p>
          Nothing in this Agreement will limit, exclude, or restrict a party's
          liability to the extent prohibited by Applicable Laws. The exclusions
          in Section 8.2 do not apply to: (a) a breach of Section 10
          (Confidentiality); (b) any indemnification obligations; (c) breaches
          of Sections 2.1 (Restrictions on Customer) or 11 (Reservation of
          Rights); or (d) Customer's infringement or misappropriation of
          Provider's intellectual property rights. Where law does not permit the
          limitation or exclusion of certain damages, that portion or type of
          liability will be limited to the extent permitted by law.
        </p>
      </div>
      <hr />
      <h2 id="9-indemnification">
        <span>9.</span> Indemnification
      </h2>
      <div className="indent">
        <h4 id="9-1-protection-by-provider">
          <span>9.1</span> Protection by Provider
        </h4>
        <p>
          Provider will indemnify, defend, and hold harmless Customer from and
          against all Provider Covered Claims made by someone other than
          Customer, Customer's Affiliates, or Users, and all out-of-pocket
          damages, awards, settlements, costs, and expenses, including
          reasonable attorneys' fees and other legal expenses, that arise from
          the Provider Covered Claims.
        </p>
        <h4 id="9-2-protection-by-customer">
          <span>9.2</span> Protection by Customer
        </h4>
        <p>
          Customer will indemnify, defend, and hold harmless Provider from and
          against all Customer Covered Claims made by someone other than
          Provider or its Affiliates, and all out-of-pocket damages, awards,
          settlements, costs, and expenses, including reasonable attorneys' fees
          and other legal expenses, that arise from the Customer Covered Claims.
        </p>
        <h4 id="9-3-procedure">
          <span>9.3</span> Procedure
        </h4>
        <p>
          The Indemnifying Party's obligations in this Section 9 are contingent
          upon the Protected Party:
        </p>
        <ol className="list-decimal pl-6" type="a">
          <li>
            promptly notifying the Indemnifying Party of each Covered Claim for
            which it seeks protection;
          </li>
          <li>
            providing reasonable assistance to the Indemnifying Party at the
            Indemnifying Party's expense; and
          </li>
          <li>
            giving the Indemnifying Party sole control over the defense and
            settlement of each Covered Claim.
          </li>
        </ol>
        <p>
          A Protected Party may participate in a Covered Claim with its own
          attorneys only at its own expense. The Indemnifying Party may not
          agree to any settlement of a Covered Claim that contains an admission
          of fault or otherwise materially and adversely impacts the Protected
          Party without the Protected Party's prior written consent.
        </p>
        <h4 id="9-4-changes-to-product">
          <span>9.4</span> Changes to Product
        </h4>
        <p>
          If required by settlement or court order, or if deemed reasonably
          necessary in response to a Provider Covered Claim, Provider may:
        </p>
        <ol className="list-decimal pl-6" type="a">
          <li>obtain the right for Customer to continue using the Product;</li>
          <li>
            replace or modify the affected component of the Product without
            materially reducing the general functionality of the Product; or
          </li>
          <li>
            if neither (a) nor (b) are reasonable, terminate the affected Order
            Form and issue a prorated refund of prepaid Fees for the remainder
            of the Subscription Period.
          </li>
        </ol>
        <h4 id="9-5-exclusions">
          <span>9.5</span> Exclusions
        </h4>
        <ol>
          <li>
            <p>
              Provider's obligations as an Indemnifying Party will not apply to
              Provider Covered Claims that result from:
            </p>
            <ol className="list-decimal pl-6" type="i">
              <li>
                modifications to the Product that were not authorized by
                Provider or that were made in compliance with Customer's
                instructions;
              </li>
              <li>
                unauthorized use of the Product, including use in violation of
                this Agreement;
              </li>
              <li>
                use of the Product in combination with items not provided by
                Provider;
              </li>
              <li>
                use of an old version of the Product where a newer release would
                avoid the Provider Covered Claim.
              </li>
            </ol>
          </li>
          <li>
            Customer's obligations as an Indemnifying Party will not apply to
            Customer Covered Claims that result from the unauthorized use of the
            Customer Content, including use in violation of this Agreement.
          </li>
        </ol>
        <h4 id="9-6-exclusive-remedy">
          <span>9.6</span> Exclusive Remedy
        </h4>
        <p>
          This Section 9 (Indemnification), together with any termination rights
          expressly stated in this Agreement, describes each Protected Party's
          exclusive remedy and each Indemnifying Party's entire liability for a
          Covered Claim.
        </p>
      </div>
      <hr />
      <h2 id="10-confidentiality">
        <span>10.</span> Confidentiality
      </h2>
      <div className="indent">
        <h4 id="10-1-non-use-and-non-disclosure">
          <span>10.1</span> Non-Use and Non-Disclosure
        </h4>
        <p>
          Except as otherwise authorized in this Agreement or as needed to
          fulfill its obligations or exercise its rights under this Agreement,
          Recipient will not (a) use Discloser's Confidential Information; nor
          (b) disclose Discloser's Confidential Information to anyone else. In
          addition, Recipient will protect Discloser's Confidential Information
          using at least the same protections Recipient uses for its own similar
          information, but no less than a reasonable standard of care. During
          the course of delivering the Product, Confidential Information may be
          disclosed by or on behalf of either party. Such Confidential
          Information may only be used for purposes related to the delivery of
          the Product or to enforce rights under this Agreement.
        </p>
        <h4 id="10-2-exclusions">
          <span>10.2</span> Exclusions
        </h4>
        <p>Confidential Information does not include information that:</p>
        <ol className="list-decimal pl-6" type="a">
          <li>
            Recipient knew without any obligation of confidentiality before
            disclosure by Discloser;
          </li>
          <li>
            is or becomes publicly known and generally available through no
            fault of Recipient;
          </li>
          <li>
            Recipient receives under no obligation of confidentiality from
            someone else who is authorized to make the disclosure; or
          </li>
          <li>
            Recipient independently developed without use of or reference to
            Discloser's Confidential Information.
          </li>
        </ol>
        <h4 id="10-3-required-disclosures">
          <span>10.3</span> Required Disclosures
        </h4>
        <p>
          Recipient may disclose Discloser's Confidential Information to the
          extent required by Applicable Laws if, unless prohibited by Applicable
          Laws, Recipient provides Discloser reasonable advance notice of the
          required disclosure and reasonably cooperates, at Discloser's expense,
          with Discloser's efforts to obtain confidential treatment for the
          Confidential Information.
        </p>
        <h4 id="10-4-permitted-disclosures">
          <span>10.4</span> Permitted Disclosures
        </h4>
        <p>
          Recipient may disclose Discloser's Confidential Information to Users,
          employees, advisors, contractors, and representatives who each have a
          need to know the Confidential Information, but only if the person or
          entity is bound by confidentiality obligations at least as protective
          as those in this Section&nbsp;10 and Recipient remains responsible for
          their compliance.
        </p>
      </div>
      <hr />
      <h2 id="11-reservation-of-rights">
        <span>11.</span> Reservation of Rights
      </h2>
      <div className="indent">
        <h4 id="11-1-ownership-of-the-product">
          <span>11.1</span> Ownership of the Product
        </h4>
        <p>
          Except for the limited license to copy and use Software and
          Documentation in Section 1.1 (Access and Use), Provider retains all
          right, title, and interest in and to the Product, whether developed
          before or after the Effective Date. This includes all AI models,
          algorithms, and any derivative works or improvements to the Product,
          regardless of how they are created or generated. Any modifications,
          adaptations, or improvements to the Product's AI models, even if
          influenced by processing Customer Content, remain the exclusive
          property of Provider. Except for the limited rights in Section 1.5
          (Customer Content) and Section 1.6 (Machine Learning), Customer
          retains all right, title, and interest in and to Customer Content.
        </p>
        <h4 id="11-2-intellectual-property">
          <span>11.2</span> Intellectual Property
        </h4>
        <p>
          Provider reserves all of its intellectual property rights in the
          Product. Using the Product does not give Customer any license or
          ownership in the Product or any associated intellectual property
          rights. If Customer believes in good faith that its copyright is being
          infringed, Customer may send Provider a notice of infringement under
          Applicable Laws at the address provided in Section 12.9 (Notices).
        </p>
      </div>
      <hr />
      <h2 id="12-general-terms">
        <span>12.</span> General Terms
      </h2>
      <div className="indent">
        <h4 id="12-1-entire-agreement">
          <span>12.1</span> Entire Agreement
        </h4>
        <p>
          This Agreement consists of the Order Form, these Terms, and any
          documents expressly incorporated by reference therein. In the event of
          any conflict or inconsistency, the Order Form will take precedence,
          followed by these Terms, and then any incorporated documents. This
          Agreement supersedes all prior or contemporaneous statements (whether
          in writing or not) about its subject. Provider expressly rejects any
          terms included in Customer's purchase order or similar document, which
          may only be used for accounting or administrative purposes. No terms
          or conditions in any Customer documentation or online vendor portal
          will apply to Customer's use of the Product unless expressly agreed to
          in a written Order Form signed by an authorized Provider
          representative.
        </p>
        <h4 id="12-2-modifications-severability-and-waiver">
          <span>12.2</span> Modifications, Severability, and Waiver
        </h4>
        <ol>
          <li>
            <strong>Modifications.</strong> For any material modification to
            this Agreement, Provider will provide Customer with at least 30
            days' advance notice. If Customer continues to use the Product after
            the notice period, such use constitutes Customer's acceptance of the
            modified terms. Non-material modifications may be made at any time
            upon notice to Customer.
          </li>
          <li>
            <strong>Severability.</strong> If any term of this Agreement is
            determined to be invalid or unenforceable by a relevant court or
            governing body, the remaining terms of this Agreement will remain in
            full force and effect.
          </li>
          <li>
            <strong>Waiver.</strong> The failure of a party to enforce a term or
            to exercise an option or right in this Agreement will not constitute
            a waiver by that party of the term, option, or right.
          </li>
        </ol>
        <h4 id="12-3-governing-law-and-chosen-courts">
          <span>12.3</span> Governing Law and Chosen Courts
        </h4>
        <p>
          This Agreement and any dispute related to it will be governed
          exclusively by the laws of the State of California, excluding its
          conflict of laws rules. All claims and disputes arising out of or
          relating to this Agreement must be litigated exclusively in the
          federal or state courts located in San Francisco County, California,
          and each party consents to personal jurisdiction in those courts.
        </p>
        <h4 id="12-4-injunctive-relief">
          <span>12.4</span> Injunctive Relief
        </h4>
        <p>
          A breach of Section 10 (Confidentiality) or the violation of a party's
          intellectual property rights may cause irreparable harm for which
          monetary damages cannot adequately compensate. Upon the actual or
          threatened breach of Section 10 (Confidentiality) or violation of a
          party's intellectual property rights, the non-breaching or
          non-violating party may seek appropriate equitable relief, including
          an injunction, in any court of competent jurisdiction without the need
          to post a bond and without limiting its other rights or remedies.
        </p>
        <h4 id="12-5-non-exhaustive-remedies">
          <span>12.5</span> Non-Exhaustive Remedies
        </h4>
        <p>
          Except where this Agreement provides for an exclusive remedy, seeking
          or exercising a remedy does not limit the other rights or remedies
          available to a party.
        </p>
        <h4 id="12-6-assignment">
          <span>12.6</span> Assignment
        </h4>
        <p>
          Neither party may assign any rights or obligations under this
          Agreement without the prior written consent of the other party.
          However, either party may assign this Agreement upon notice if the
          assigning party undergoes a merger, change of control, reorganization,
          or sale of all or substantially all its equity, business, or assets to
          which this Agreement relates. Any attempted but non-permitted
          assignment is void. This Agreement will be binding upon and inure to
          the benefit of the parties and their permitted successors and assigns.
        </p>
        <h4 id="12-7-beta-products">
          <span>12.7</span> Beta Products
        </h4>
        <p>
          If Provider gives Customer access to a Beta Product, the Beta Product
          is provided “AS IS,” and any warranty under Section 6.3 (From
          Provider) does not apply to Beta Products. Customer acknowledges that
          Beta Products are experimental in nature and may be modified or
          removed by Provider at its discretion with or without notice.
        </p>
        <h4 id="12-8-logo-rights">
          <span>12.8</span> Logo Rights
        </h4>
        <p>
          Provider may identify Customer and use Customer's name and logo in
          marketing to identify Customer as a user of Provider's products and
          services.
        </p>
        <h4 id="12-9-notices">
          <span>12.9</span> Notices
        </h4>
        <p>
          Any notice, request, or approval about this Agreement must be in
          writing and sent to the recipient's address (or email address) as
          specified in an Order Form or as otherwise communicated. Notices will
          be deemed given (a) upon confirmed delivery if by email, registered or
          certified mail, or personal delivery; or (b) two days after mailing if
          by overnight commercial delivery.
        </p>
        <p>
          If you believe in good faith that your copyright is being infringed,
          you may send a notice to the following address:
          <br />
          <strong>440 N. Barranca Ave. #1890, Covina, CA 91723</strong>
        </p>
        <h4 id="12-10-independent-contractors">
          <span>12.10</span> Independent Contractors
        </h4>
        <p>
          The parties are independent contractors, not agents, partners, or
          joint venturers. Neither party is authorized to bind the other to any
          liability or obligation.
        </p>
        <h4 id="12-11-no-third-party-beneficiary">
          <span>12.11</span> No Third-Party Beneficiary
        </h4>
        <p>There are no third-party beneficiaries of this Agreement.</p>
        <h4 id="12-12-force-majeure">
          <span>12.12</span> Force Majeure
        </h4>
        <p>
          Neither party will be liable for a delay or failure to perform its
          obligations under this Agreement if caused by a Force Majeure Event.
          However, this Section does not excuse Customer's obligations to pay
          Fees.
        </p>
        <h4 id="12-13-export-controls">
          <span>12.13</span> Export Controls
        </h4>
        <p>
          Customer may not remove or export from the United States or allow the
          export or re-export of the Product or any related technology or
          materials in violation of any restrictions, laws, or regulations of
          the United States Department of Commerce, OFAC, or any other United
          States or foreign agency or authority. Customer represents and
          warrants that it is not (a) a resident or national of an Embargoed
          Country; (b) an entity organized under the laws of an Embargoed
          Country; (c) designated on any list of prohibited, restricted, or
          sanctioned parties maintained by the U.S. government or agencies or
          other applicable governments or agencies; nor (d) 50% or more owned by
          any party designated on any of the above lists. Provider may terminate
          this Agreement immediately without notice or liability to comply, as
          determined in Provider's sole discretion, with applicable export
          controls and sanctions laws and regulations.
        </p>
        <h4 id="12-14-government-rights">
          <span>12.14</span> Government Rights
        </h4>
        <p>
          The Product and Software are deemed “commercial items” or “commercial
          computer software” according to FAR section 12.212 and DFAR section
          227.7202, and the Documentation is “commercial computer software
          documentation” according to DFAR section 252.227-7014(a)(1) and (5).
          Any use, modification, reproduction, release, performance, display, or
          disclosure of the Product by the U.S. Government will be governed
          solely by the terms of this Agreement, and all other use is
          prohibited.
        </p>
        <h4 id="12-15-anti-bribery">
          <span>12.15</span> Anti-Bribery
        </h4>
        <p>
          Neither party will take any action that would be a violation of any
          Applicable Laws that prohibit the offering, giving, promising to offer
          or give, or receiving, directly or indirectly, money or anything of
          value to any third party to assist a party in retaining or obtaining
          business. Examples of these kinds of laws include the U.S. Foreign
          Corrupt Practices Act and the UK Bribery Act 2010.
        </p>
        <h4 id="12-16-titles-and-interpretation">
          <span>12.16</span> Titles and Interpretation
        </h4>
        <p>
          Section titles are for convenience and reference only. All uses of
          “including” and similar phrases are non-exhaustive and without
          limitation. The United Nations Convention for the International Sale
          of Goods and the Uniform Computer Information Transaction Act do not
          apply to this Agreement.
        </p>
        <h4 id="12-17-signature">
          <span>12.17</span> Signature
        </h4>
        <p>
          This Agreement may be signed in counterparts, including by electronic
          copies or acceptance mechanism. Each copy will be deemed an original,
          and all copies, when taken together, will constitute the same
          agreement.
        </p>
      </div>
      <hr />
      <h2 id="13-definitions">
        <span>13.</span> Definitions
      </h2>
      <ol>
        <li>
          <strong>“Affiliate”</strong> means any entity that, directly or
          indirectly, controls, is under the control of, or is under common
          control with a party, where control means having more than fifty
          percent (50%) of the voting stock or other ownership interest.
        </li>
        <li>
          <strong>“Agreement”</strong> means, collectively, these terms and each
          Order Form (including any attachments or documents referenced in an
          Order Form) that incorporate these terms by reference.
        </li>
        <li>
          <strong>“Applicable Data Protection Laws”</strong> means the
          Applicable Laws that govern how the Product may process or use an
          individual's personal information, personal data, personally
          identifiable information, or other similar term.
        </li>
        <li>
          <strong>“Applicable Laws”</strong> means the laws, rules, regulations,
          court orders, and other binding requirements of a relevant
          governmental authority that apply to or govern Provider or Customer,
          including any applicable export control or sanctions laws.
        </li>
        <li>
          <strong>“Beta Product”</strong> means an early or prerelease feature
          or version of the Product that is identified as beta or similar, or a
          version of the Product that is not generally available.
        </li>
        <li>
          <strong>“Cloud Service”</strong> means, if applicable, the hosted
          services described in the Order Form.
        </li>
        <li>
          <strong>“Confidential Information”</strong> means information in any
          form disclosed by or on behalf of a Discloser, including before the
          Effective Date, to a Recipient in connection with this Agreement that
          (a) the Discloser identifies as “confidential,” “proprietary,” or the
          like; or (b) should be reasonably understood as confidential or
          proprietary due to its nature and the circumstances of its disclosure.
          Confidential Information includes the existence and content of this
          Agreement, non-public Customer Content, and non-public information
          about the Product.
        </li>
        <li>
          <strong>“Customer Content”</strong> means data, information, or
          materials submitted by or on behalf of Customer or Users to the
          Product, but excludes Feedback.
        </li>
        <li>
          <strong>“Discloser”</strong> means a party when the party is providing
          or disclosing Confidential Information to the other party.
        </li>
        <li>
          <strong>“Documentation”</strong> means the usage manuals and
          instructional materials for the Product that are made available by
          Provider.
        </li>
        <li>
          <strong>“Effective Date”</strong> means the date these terms become
          effective as stated in an Order Form or as otherwise indicated in
          writing by the parties.
        </li>
        <li>
          <strong>“Feedback”</strong> means suggestions, feedback, or comments
          about the Product or related offerings.
        </li>
        <li>
          <strong>“Fees”</strong> means the applicable amounts described in an
          Order Form or otherwise specified for the Product.
        </li>
        <li>
          <strong>“Force Majeure Event”</strong> means an unforeseen event
          outside a party's reasonable control where the affected party took
          reasonable measures to avoid or mitigate the impacts of the event.
          Examples include major natural disasters, war, pandemic, riot, act of
          terrorism, or public utility or internet failure.
        </li>
        <li>
          <strong>“GDPR”</strong> means Regulation 2016/679 of the European
          Union as implemented by local law in relevant European Union member
          nations and as incorporated into the law of the United Kingdom.
        </li>
        <li>
          <strong>“High Risk Activity”</strong> means any situation where the
          use or failure of the Product could be reasonably expected to lead to
          death, bodily injury, or environmental damage, including medical
          life-support technology, emergency response services, nuclear
          facilities operation, or air traffic control.
        </li>
        <li>
          <strong>“Indemnifying Party”</strong> means a party to this Agreement
          when the party is providing protection for a particular Covered Claim.
        </li>
        <li>
          <strong>“Key Terms”</strong> means any document containing Variables
          or key legal details that govern an Order Form and references these
          Terms.
        </li>
        <li>
          <strong>“Machine Learning”</strong> has the meaning described in
          Section&nbsp;1.6 of this Agreement.
        </li>
        <li>
          <strong>“Order Form”</strong> means a document signed or
          electronically accepted by the parties that references this Agreement
          and sets forth business details (such as fees, subscription period,
          etc.) for Customer's use of the Product.
        </li>
        <li>
          <strong>“Personal Data”</strong> has the meaning set forth in
          Applicable Data Protection Laws for personal information, personal
          data, personally identifiable information, or other similar term.
        </li>
        <li>
          <strong>“Product”</strong> means, collectively, the Cloud Service (if
          applicable), Software, and Documentation, and includes any related
          websites, applications, or services owned or operated by Provider that
          link to or reference this Agreement.
        </li>
        <li>
          <strong>“Prohibited Data”</strong> means (a) patient, medical, or
          other protected health information regulated by the Health Insurance
          Portability and Accountability Act; (b) credit, debit, bank account,
          or other financial account numbers; (c) social security numbers,
          driver's license numbers, or other unique and private government ID
          numbers; (d) special categories of data as defined in the GDPR; and
          (e) other similar categories of sensitive information set forth in
          Applicable Data Protection Laws.
        </li>
        <li>
          <strong>“Recipient”</strong> means a party to this Agreement when the
          party receives Confidential Information from the other party.
        </li>
        <li>
          <strong>“Software”</strong> means the client-side software or
          applications made available by Provider for Customer to install or
          execute as part of the Product.
        </li>
        <li>
          <strong>“Subscription Period”</strong> means the timeframe specified
          in an Order Form during which Customer is authorized to use the
          Product.
        </li>
        <li>
          <strong>“Usage Data”</strong> means data and information about the
          provision, use, and performance of the Product and related offerings
          based on Customer's or User's use of the Product.
        </li>
        <li>
          <strong>“User”</strong> means any individual who uses the Product on
          Customer's behalf or through Customer's account.
        </li>
      </ol>
    </>
  );
}
